<template>
  <div class="row">
    <div class="col-auto">
      <svg
        :height="containerHeight"
        :width="containerWidth"
        class="d-block mx-auto"
        id="chart"
      ></svg>
      <div class="text-center text-secondary">
        <p><small>Efficacy / Safety Rating (0=lowest,10=highest)</small></p>
      </div>
    </div>
    <div class="col">
      <div class="d-flex flex-row">
        <div :style="{ width: containerWidth/2 + 'px' }" class="legend mx-auto mt-2">
       <div class="mb-2">   <b >Efficacy First</b></div>
          <ul class="pl-0"  >
            <li
                :key="index"
                class="legend__item"
                :class="{ pointer: allowDragAll }"
                v-for="(legendPoint, index) of legendPointsEfficacyFirst"
                @click="chooseTreatment(legendPoint.id)"
            >
              <div>
                <i
                    :style="{ 'background-color': legendPoint.color }"
                    class="legend__color"
                ></i>
              </div>
              <div class="legend__label">
                {{ legendPoint.name }} - ({{ legendPoint.xCoord }}/{{ legendPoint.yCoord }})
              </div>
            </li>
          </ul>
        </div>
        <div :style="{ width: containerWidth/2 + 'px' }"  class="legend mx-auto mt-2">
         <div class="mb-2"> <b >Safety First</b></div>
          <ul class="pl-0">
            <li
                :key="index"
                class="legend__item"
                :class="{ pointer: allowDragAll }"
                v-for="(legendPoint, index) of legendPointsSafetyFirst"
                @click="chooseTreatment(legendPoint.id)"
            >
              <div>
                <i
                    :style="{ 'background-color': legendPoint.color }"
                    class="legend__color"
                ></i>
              </div>
              <div class="legend__label">
                {{ legendPoint.name }} - ({{ legendPoint.yCoord }}/{{ legendPoint.xCoord }})
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
// @ts-ignore - Using D3 v3 which doesn't match current @types/d3
import * as d3 from "d3";
import { Point } from "@/entities/chart/Point";
import { PointsCollection } from "@/entities/chart/PointsCollection";

@Component
export default class AppScatterChart extends Vue {
  private containerWidth: number = 500;
  private containerHeight: number = 470;

  legendPointsSafetyFirst: Point[] = [];
  legendPointsEfficacyFirst: Point[] = [];

  @Prop({ default: new PointsCollection(), type: PointsCollection })
  pointsCollection!: PointsCollection;
  localPointsCollection: PointsCollection = this.pointsCollection;

  @Prop({ required: false })
  point?: Point;
  localPoint?: Point = this.point;
  @Watch("localPoint")
  onLocalPointChanged() {
    this.$emit("update:point", this.localPoint);
  }
  @Watch("point")
  onPointChanged() {
    this.localPoint = this.point;
  }

  @Prop({ default: "", required: true, type: String })
  xAxisLabel!: string;

  @Prop({ default: "", required: true, type: String })
  yAxisLabel!: string;

  @Prop({ default: false, type: Boolean })
  allowDragAll!: boolean;

  @Watch("pointsCollection", { deep: true })
  onPointsCollectionChange() {
    this.localPointsCollection = this.pointsCollection;
    this.renderChart();
  }

  @Watch("localPointsCollection", { deep: true })
  onLocalCollectionChange() {
    this.$emit("input:pointsCollection", this.localPointsCollection);
  }

  created(){
    this.calculatePoints(this.localPointsCollection.getPoints());
  }

  calculatePoints(points: Point[]) {
    console.log('calculatePoints');
    this.legendPointsEfficacyFirst = [];
    this.legendPointsSafetyFirst = [];

    if (!this.allowDragAll && this.localPoint) {
      points.push(this.localPoint);
    }

    this.legendPointsEfficacyFirst = Object.assign([], points).sort((point1: Point, point2: Point) => {
      const point2Rating = point2.xCoord + point2.yCoord;
      const point1Rating = point1.xCoord + point1.yCoord;
      if(point2.xCoord === point1.xCoord){
        return point2Rating - point1Rating;
      }
      return point2.xCoord - point1.xCoord;
    });


    this.legendPointsSafetyFirst = Object.assign([], points).sort((point1: Point, point2: Point) => {
      const point2Rating = point2.xCoord + point2.yCoord;
      const point1Rating = point1.xCoord + point1.yCoord;
      if(point2Rating === point1Rating){
        return point2.yCoord - point1.yCoord;
      }

      return point2Rating - point1Rating;
    });
  }

  renderChart(): void {
    const vueInstance = this;

    const margin = { top: 20, right: 20, bottom: 35, left: 45 };
    const width = this.containerWidth - margin.left - margin.right;
    const height = this.containerHeight - margin.top - margin.bottom;

    const svg = d3.select("#chart");
    svg.selectAll("*").remove();

    // D3 v3 syntax for scales
    // @ts-ignore - D3 v3 API
    const x = d3.scale.linear().range([0, width]);
    // @ts-ignore - D3 v3 API
    const y = d3.scale.linear().range([height, 0]);

    // D3 v3 syntax for axes
    // @ts-ignore - D3 v3 API
    const xAxisFactory = () => d3.svg.axis().scale(x).orient("bottom");
    // @ts-ignore - D3 v3 API
    const yAxisFactory = () => d3.svg.axis().scale(y).orient("left");

    // @ts-ignore - D3 v3 API
    const drag = d3.behavior
      .drag()
      // @ts-ignore
      .on("drag", dragged)
      // @ts-ignore
      .on("dragend", dragended);

    const focus = svg
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    focus.on("touchstart", nozoom).on("touchmove", nozoom);

    // add grid xAxis
    focus
      .append("g")
      .attr("class", "grid")
      .attr("transform", "translate(0," + height + ")")
      .call(
        xAxisFactory()
          .ticks(10)
          .tickSize(-height)
          .tickFormat("")
      );

    // add grid yAxis
    focus
      .append("g")
      .attr("class", "grid")
      .call(
        yAxisFactory()
          .ticks(10)
          .tickSize(-width)
          .tickFormat("")
      );

    x.domain([0, 10]);
    y.domain([0, 10]);

    focus
      .selectAll("circle")
      .data(this.localPointsCollection.getPoints())
      .enter()
      .append("circle")
      .on("click", (point: Point) => vueInstance.chooseTreatment(point.id))
      .attr("class", this.allowDragAll ? "draggable" : "")
      .attr("r", (point: Point) => {
        return point.radius;
      })
      .attr("id", (point: Point) => {
        return point.id;
      })
      .attr("cx", (point: Point) => x(point.xCoord) || 0)
      .attr("cy", (point: Point) => y(point.yCoord) || 0)
      .style("cursor", this.allowDragAll ? "pointer" : "")
      .style("fill", (point: Point) => {
        return point.color;
      })
      .html((point: Point) => {
        return `<title>${point.name} - ${point.xCoord}/${point.yCoord}</title>`;
      });

    if (this.localPoint) {
      focus
        .data([this.localPoint.getCoords()])
        .append("circle")
        .attr("class", "draggable")
        .attr("r", this.localPoint.radius)
        .attr("cx", x(this.localPoint.xCoord) || 0)
        .attr("cy", y(this.localPoint.yCoord) || 0)
        .style("cursor", "pointer")
        .style("fill", this.localPoint.color)
        .html(
          `<title>${this.localPoint.name} - ${this.localPoint.xCoord}/${this.localPoint.yCoord}</title>`
        );
    }

    focus.selectAll(".draggable").call(drag);

    // add xAxis
    focus
      .append("g")
      .attr("transform", "translate(0," + height + ")")
      .call(xAxisFactory());

    // add yAxis
    focus.append("g").call(yAxisFactory());

    // xAxis label
    focus
      .append("text")
      .attr(
        "transform",
        "translate(" + width / 2 + " ," + (height + margin.bottom - 5) + ")"
      )
      .style("text-anchor", "middle")
      .text(this.xAxisLabel);

    //yAxis label
    focus
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left + 5)
      .attr("x", 0 - height / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text(this.yAxisLabel);

    function dragged(this: any, coords: [number, number], i: any, nodes: any) {
      function roundHalf(num: number) {
        return Math.round(num * 2) / 2;
      }

      function constraintAndScale(
        value: number,
        min: number,
        max: number,
        scaleFunc: Function
      ): number {
        const scaledValue = scaleFunc(value);

        if (scaledValue < min) return min;
        if (scaledValue > max) return max;

        return roundHalf(scaledValue);
      }

      const xAxisDomainCoord = constraintAndScale(d3.event.x, 0, 10, x.invert);
      const yAxisDomainCoord = constraintAndScale(d3.event.y, 0, 10, y.invert);

      if (
        vueInstance.localPointsCollection.isNearOfAnyPoint([
          xAxisDomainCoord,
          yAxisDomainCoord
        ])
      )
        return;

      coords[0] = xAxisDomainCoord;
      coords[1] = yAxisDomainCoord;

      const xAxisCoord = constraintAndScale(xAxisDomainCoord, 0, width, x);
      const yAxisCoord = constraintAndScale(yAxisDomainCoord, 0, height, y);

      if (vueInstance.allowDragAll) {
        // For D3 v3, nodes[i] might need to be accessed differently
        d3.select(nodes[i] || this)
          .attr("cx", xAxisCoord)
          .attr("cy", yAxisCoord);
      } else {
        // @ts-ignore
        d3.select(this)
          .attr("cx", xAxisCoord)
          .attr("cy", yAxisCoord);
      }
    }

    function dragended(this: any, coords: [number, number], i: any, nodes: any) {
      const attrId = d3.select(nodes[i] || this).attr("id");
      const pointId = parseInt(attrId);
      if (coords[0] && coords[1]) {
        vueInstance.$emit("pointCoordsChanged", pointId, coords);
        vueInstance.localPointsCollection.update(pointId, coords);
        vueInstance.calculatePoints(vueInstance.localPointsCollection.getPoints());
      }
    }

    function nozoom() {
      d3.event.preventDefault();
    }
  }

  chooseTreatment(pointId: number) {
    if (!this.allowDragAll) {
      return;
    }

    let points = this.localPointsCollection
      .getPoints()
      .filter(point => point.color !== point.mainColor || point.id === pointId);

    points.forEach(point => {
      point.color = pointId === point.id ? "#000000" : point.mainColor;
      point.radius = pointId === point.id ? 11 : point.mainRadius;
    });

    this.calculatePoints(this.localPointsCollection.getPoints());
    this.renderChart();
  }

  mounted() {
    this.renderChart();
  }
}
</script>

<style>
.grid line {
  stroke: lightgrey;
  stroke-opacity: 0.7;
  shape-rendering: crispEdges;
}

.grid path {
  stroke-width: 0;
}

.legend {
  list-style: none;
}

.legend__item {
  display: flex;
  align-items: center;
  padding: 2px 0;
}

.legend__color {
  height: 20px;
  width: 20px;
  display: inline-block;
  border-radius: 50%;
  margin-right: 5px;
}

.pointer {
  cursor: pointer;
}
</style>